'use client';

import { c as _c } from "react/compiler-runtime";
import FirstScreen from '@app/registration/(with-padding)/_basic/FirstScreen';
import SecondScreen from '@app/registration/(with-padding)/_basic/SecondScreen';
import { initialFormValues } from '@app/registration/(with-padding)/_basic/initialFormValues';
import RegistrationPageSkeleton from '@app/registration/(with-padding)/_components/registrationPageSkeleton';
import { INLink } from '@components/INLink';
import { toFormErrorResponse } from '@data/api/mappers/toFormErrorResponse';
import { useBasicRegistrationMutation } from '@data/hooks/registration/useBasicRegistrationMutation';
import { useRegistrationWorkflow } from '@data/hooks/registration/useWorkflow';
import { Button, ButtonSet, Typography } from '@internations/ds';
import { contact, login, registration } from '@routes/catalog';
import { toURL } from '@routes/utils/toURL';
import { useINAppRouter } from '@routes/utils/useINAppRouter';
import { trackEvent as trackGAEvent } from '@tracking/gtag';
import { Form, Formik, FormikProps, useFormikContext } from '@util/Formik';
import alreadyAMemberLabel from '@util/labels/alreadyAMember';
import joinNowLabel from '@util/labels/joinNow';
import loginNowLabel from '@util/labels/loginNow';
import locationSelectorErrorLabel from '@util/labels/startTypingAndSelectACity';
import { getItem, getItemFromSessionStorage, removeItem, setItem } from '@util/localStorage';
import { BasicFormStepType, BasicFormValues, BasicRegistrationFormErrorResponse, BasicRegistrationFormErrors, ErrorRegistrationFormValues, transformDataToSubmit, validationSchema } from '@util/registration/BasicFormModel';
import { FormikErrors, FormikTouched } from 'formik';
import { omit } from 'lodash-es';
import { ReactElement, useRef, useState } from 'react';
import { useEffectOnceWhen } from 'rooks';
import { ValidationError } from 'yup';
import dayjs from '@util/dayjs';
const FORM_DATA_STORAGE_KEY = 'registrationFormTempData';
type RegistrationFormProps = {
  isSubmitting: boolean;
  currentStep: BasicFormStepType;
  backendErrors?: BasicRegistrationFormErrors;
  inviter?: TMember;
  isEmailReadOnly?: boolean;
  setBackendErrors: (errors: BasicRegistrationFormErrors) => void;
};
const getButtonAction = (currentStep: BasicFormStepType): 'submit' | 'next' => {
  return currentStep === 'SecondStep' ? 'submit' : 'next';
};
function RegistrationForm(t0) {
  const $ = _c(31);
  const {
    isSubmitting,
    backendErrors,
    setBackendErrors,
    inviter,
    isEmailReadOnly,
    currentStep
  } = t0;
  const isSecondStep = currentStep === "SecondStep";
  const buttonText = getButtonAction(currentStep).replace(/^./, _temp);
  const context = useFormikContext();
  let t1;
  if ($[0] !== inviter || $[1] !== isEmailReadOnly) {
    t1 = <FirstScreen inviter={inviter} isEmailReadOnly={isEmailReadOnly} />;
    $[0] = inviter;
    $[1] = isEmailReadOnly;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  let t2;
  if ($[3] !== backendErrors) {
    t2 = <SecondScreen backendErrors={backendErrors} />;
    $[3] = backendErrors;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  let t3;
  if ($[5] !== t1 || $[6] !== t2) {
    t3 = {
      FirstStep: t1,
      SecondStep: t2
    };
    $[5] = t1;
    $[6] = t2;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  const STEP_TO_SCREEN = t3;
  const T0 = Form;
  const t4 = "BasicRegistrationForm";
  const t5 = `flex flex-col gap-4 ${isSecondStep ? "gap-3" : "gap-4"}`;
  const t6 = STEP_TO_SCREEN[currentStep];
  const T1 = ButtonSet;
  const T2 = Button;
  const t7 = "t-next-step";
  const t8 = "submit";
  const t9 = buttonText.toLowerCase();
  let t10;
  if ($[8] !== backendErrors || $[9] !== context || $[10] !== currentStep || $[11] !== isSecondStep || $[12] !== setBackendErrors) {
    t10 = () => {
      const bornIn = context.getFieldMeta("bornIn");
      const livingIn = context.getFieldMeta("livingIn");
      if (currentStep === "FirstStep") {
        setBackendErrors({
          ...backendErrors,
          bornIn: undefined,
          livingIn: undefined
        });
      }
      if (currentStep === "SecondStep" && !(bornIn.value && livingIn.value)) {
        const error = {
          bornIn: bornIn.value ? undefined : locationSelectorErrorLabel,
          livingIn: livingIn.value ? undefined : locationSelectorErrorLabel
        };
        setBackendErrors({
          ...backendErrors,
          ...error
        });
      }
      trackGAEvent({
        eventName: "button_clicked",
        eventParams: {
          event_category: "registration",
          event_label: isSecondStep ? "submit_rs06b" : "next_rs06b"
        }
      });
    };
    $[8] = backendErrors;
    $[9] = context;
    $[10] = currentStep;
    $[11] = isSecondStep;
    $[12] = setBackendErrors;
    $[13] = t10;
  } else {
    t10 = $[13];
  }
  let t11;
  if ($[14] !== T2 || $[15] !== buttonText || $[16] !== isSubmitting || $[17] !== t10 || $[18] !== t9) {
    t11 = <T2 className={t7} disabled={isSubmitting} type={t8} data-testid={t9} onClick={t10}>{buttonText}</T2>;
    $[14] = T2;
    $[15] = buttonText;
    $[16] = isSubmitting;
    $[17] = t10;
    $[18] = t9;
    $[19] = t11;
  } else {
    t11 = $[19];
  }
  let t12;
  if ($[20] !== T1 || $[21] !== t11) {
    t12 = <T1>{t11}</T1>;
    $[20] = T1;
    $[21] = t11;
    $[22] = t12;
  } else {
    t12 = $[22];
  }
  let t13;
  if ($[23] !== currentStep) {
    t13 = currentStep === "FirstStep" ? <Typography variant="caption" className="container-app-only text-center">{alreadyAMemberLabel} <INLink route={login}>{loginNowLabel}</INLink></Typography> : null;
    $[23] = currentStep;
    $[24] = t13;
  } else {
    t13 = $[24];
  }
  let t14;
  if ($[25] !== T0 || $[26] !== t12 || $[27] !== t13 || $[28] !== t5 || $[29] !== t6) {
    t14 = <T0 id={t4} className={t5}>{t6}{t12}{t13}</T0>;
    $[25] = T0;
    $[26] = t12;
    $[27] = t13;
    $[28] = t5;
    $[29] = t6;
    $[30] = t14;
  } else {
    t14 = $[30];
  }
  return t14;
}
function _temp(c) {
  return c.toUpperCase();
}
export function transformBackendErrors(errorData: TFormErrorResponse<BasicRegistrationFormErrorResponse, ErrorRegistrationFormValues>): BasicRegistrationFormErrors {
  const {
    namedErrors: backendErrors
  } = errorData;
  const errors: BasicRegistrationFormErrors & ErrorRegistrationFormValues = {
    ...backendErrors
  };
  const emailAlreadyTakenCode = 'EMAIL_ALREADY_TAKEN';
  const registrationDeclinedCode = 'REGISTRATION_DECLINED';
  if (backendErrors.email === emailAlreadyTakenCode) {
    errors.email = <span>
                {`This Email address has already been registered. Please contact our `}
                <INLink route={contact}>Community Support Team</INLink>.
            </span>;
  }
  if (backendErrors.email === registrationDeclinedCode) {
    errors.email = <span>
                {`Your membership has been declined. For further information, please get in touch with our `}
                <INLink route={contact}>Community Support Team</INLink>.
            </span>;
  }
  if (backendErrors['password.password']) {
    errors.password = backendErrors['password.password'];
    delete errors['password.password'];
  }
  return errors;
}
const useInitialData = t0 => {
  const $ = _c(4);
  const {
    currentStep,
    inviteeFirstName,
    inviteeLastName,
    inviteeEmail
  } = t0;
  const storedValues = getItem(FORM_DATA_STORAGE_KEY, true) as BasicFormValues;
  if (storedValues) {
    storedValues.birthday = storedValues.birthday ? dayjs(storedValues.birthday) : undefined;
  }
  const shouldCheckSessionStorage = !inviteeEmail;
  let t1;
  if ($[0] !== shouldCheckSessionStorage) {
    t1 = () => shouldCheckSessionStorage ? false : undefined;
    $[0] = shouldCheckSessionStorage;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const [storedEmail, setStoredEmail] = useState(t1);
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = () => {
      const sessionEmail = getItemFromSessionStorage("registration_email") as string | undefined;
      setStoredEmail(sessionEmail);
      if (sessionEmail) {
        removeItem("registration_email", true);
      }
    };
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  useEffectOnceWhen(t2, shouldCheckSessionStorage);
  if (storedEmail === false) {
    let t3;
    if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
      t3 = {
        initialValues: initialFormValues,
        initialErrors: {},
        initialTouched: {}
      };
      $[3] = t3;
    } else {
      t3 = $[3];
    }
    return t3;
  }
  const initialValues = {
    ...initialFormValues,
    firstName: inviteeFirstName ?? initialFormValues.firstName,
    lastName: inviteeLastName ?? initialFormValues.lastName,
    email: inviteeEmail ?? storedEmail ?? initialFormValues.email,
    ...storedValues
  };
  const initialErrors = {};
  const initialTouched = {};
  if (currentStep === "FirstStep" && initialValues.email) {
    ;
    try {
      validationSchema.FirstStep.validateSyncAt("email", initialValues);
    } catch (t3) {
      const e = t3;
      const error = e as ValidationError;
      initialErrors.email = error.message;
      initialTouched.email = true;
    }
  }
  return {
    initialValues,
    initialErrors,
    initialTouched
  };
};
function RegistrationScreen(t0) {
  const $ = _c(32);
  const {
    step
  } = t0;
  const {
    data: workflowData,
    isPending,
    navigateWorkflow
  } = useRegistrationWorkflow("basic");
  const data = workflowData as TBasicStepWorkflowData | undefined;
  const currentStep = step === "second" ? "SecondStep" : "FirstStep";
  let t1;
  if ($[0] !== currentStep) {
    t1 = getButtonAction(currentStep);
    $[0] = currentStep;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const buttonAction = t1;
  const {
    go
  } = useINAppRouter();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [backendErrors, setBackendErrors] = useState(undefined);
  const formikRef = useRef(null);
  let t2;
  if ($[2] !== navigateWorkflow) {
    t2 = data_0 => {
      navigateWorkflow(data_0?.workflow);
    };
    $[2] = navigateWorkflow;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== go) {
    t3 = error => {
      const statusCode = error.response?.status;
      if (statusCode === 406) {
        const errorData = toFormErrorResponse(error.response?.data?.form);
        const errors = transformBackendErrors(errorData);
        setBackendErrors(errors);
        formikRef.current?.setErrors(errors as FormikErrors<BasicFormValues>);
        const firstStepFieldNames = ["firstName", "lastName", "email", "password"];
        if (Object.keys(errors).some(errorKey => firstStepFieldNames.includes(errorKey))) {
          go(registration.params({
            ...Object.fromEntries(new URLSearchParams(window.location.search).entries()),
            step: "first"
          }));
        }
      }
      setIsSubmitting(false);
    };
    $[4] = go;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] !== t2 || $[7] !== t3) {
    t4 = {
      onSuccess: t2,
      onError: t3
    };
    $[6] = t2;
    $[7] = t3;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  const {
    mutate: postBasicRegistration
  } = useBasicRegistrationMutation(t4);
  let t5;
  if ($[9] !== buttonAction || $[10] !== postBasicRegistration) {
    t5 = async values => {
      if (buttonAction === "next") {
        const tempFormData = omit(values, ["password", "repeatPassword"]);
        setItem(FORM_DATA_STORAGE_KEY, tempFormData, true);
        const url = toURL(window.location.href);
        url.searchParams.set("step", "second");
        window.history.replaceState(null, "", url.toString());
        return;
      }
      if (buttonAction === "submit") {
        setIsSubmitting(true);
        const dataToSubmit = transformDataToSubmit(values);
        postBasicRegistration(dataToSubmit);
        removeItem(FORM_DATA_STORAGE_KEY, true);
      }
    };
    $[9] = buttonAction;
    $[10] = postBasicRegistration;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  const onSubmit = t5;
  const t6 = data?.invitee?.firstName;
  const t7 = data?.invitee?.lastName;
  const t8 = data?.invitee?.email;
  let t9;
  if ($[12] !== currentStep || $[13] !== t6 || $[14] !== t7 || $[15] !== t8) {
    t9 = {
      currentStep,
      inviteeFirstName: t6,
      inviteeLastName: t7,
      inviteeEmail: t8
    };
    $[12] = currentStep;
    $[13] = t6;
    $[14] = t7;
    $[15] = t8;
    $[16] = t9;
  } else {
    t9 = $[16];
  }
  const {
    initialValues,
    initialErrors,
    initialTouched
  } = useInitialData(t9);
  if (isPending) {
    let t10;
    if ($[17] === Symbol.for("react.memo_cache_sentinel")) {
      t10 = <RegistrationPageSkeleton />;
      $[17] = t10;
    } else {
      t10 = $[17];
    }
    return t10;
  }
  if (!data) {
    return null;
  }
  let t10;
  if ($[18] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = <title>{joinNowLabel}</title>;
    $[18] = t10;
  } else {
    t10 = $[18];
  }
  const t11 = validationSchema[currentStep];
  let t12;
  if ($[19] !== backendErrors || $[20] !== currentStep || $[21] !== data?.inviter || $[22] !== data?.isEmailReadOnly || $[23] !== isSubmitting) {
    t12 = () => <RegistrationForm isSubmitting={isSubmitting} currentStep={currentStep} inviter={data?.inviter} isEmailReadOnly={data?.isEmailReadOnly} backendErrors={backendErrors} setBackendErrors={setBackendErrors} />;
    $[19] = backendErrors;
    $[20] = currentStep;
    $[21] = data?.inviter;
    $[22] = data?.isEmailReadOnly;
    $[23] = isSubmitting;
    $[24] = t12;
  } else {
    t12 = $[24];
  }
  let t13;
  if ($[25] !== initialErrors || $[26] !== initialTouched || $[27] !== initialValues || $[28] !== onSubmit || $[29] !== t11 || $[30] !== t12) {
    t13 = <>{t10}<Formik initialValues={initialValues} validationSchema={t11} initialErrors={initialErrors} initialTouched={initialTouched} onSubmit={onSubmit} innerRef={formikRef}>{t12}</Formik></>;
    $[25] = initialErrors;
    $[26] = initialTouched;
    $[27] = initialValues;
    $[28] = onSubmit;
    $[29] = t11;
    $[30] = t12;
    $[31] = t13;
  } else {
    t13 = $[31];
  }
  return t13;
}
export default RegistrationScreen;